import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 15000,
});

// Auth token injection
API.interceptors.request.use(config => {
  const token = localStorage.getItem('qaya_token') || localStorage.getItem('qaya_admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Response error handling
API.interceptors.response.use(
  res => res.data,
  err => {
    const message = err.response?.data?.error || 'Something went wrong';
    if (err.response?.status === 401) {
      localStorage.removeItem('qaya_token');
      localStorage.removeItem('qaya_admin_token');
      window.location.href = '/login';
    }
    return Promise.reject(new Error(message));
  }
);

// ─── PUBLIC API ────────────────────────────────────────────
export const authAPI = {
  register: (data) => API.post('/auth/register', data),
  login: (data) => API.post('/auth/login', data),
  getProfile: () => API.get('/auth/profile'),
  updateProfile: (data) => API.put('/auth/profile', data),
};

export const addressesAPI = {
  getAll:  () => API.get('/auth/addresses'),
  create:  (data) => API.post('/auth/addresses', data),
  update:  (id, data) => API.put(`/auth/addresses/${id}`, data),
  delete:  (id) => API.delete(`/auth/addresses/${id}`),
};

export const productsAPI = {
  getAll: (params) => API.get('/products', { params }),
  getBySlug: (slug) => API.get(`/products/${slug}`),
};

export const categoriesAPI = {
  getAll: () => API.get('/categories'),
};

export const ordersAPI = {
  create: (data) => API.post('/orders', data),
  getMyOrders: () => API.get('/orders/my'),
  getByNumber: (num) => API.get(`/orders/${num}`),
};

export const paymentAPI = {
  initiate: (data) => API.post('/payment/initiate', data),
  verify: (data) => API.post('/payment/verify', data),
};

export const couponsAPI = {
  validate: (data) => API.post('/coupons/validate', data),
};

export const reviewsAPI = {
  create: (data) => API.post('/reviews', data),
};

export const newsletterAPI = {
  subscribe: (email) => API.post('/newsletter/subscribe', { email }),
};

export const settingsAPI = {
  getPublic: () => API.get('/settings/public'),
};

// ─── ADMIN API ─────────────────────────────────────────────
export const adminAuth = {
  login: (data) => API.post('/admin/login', data),
  getProfile: () => API.get('/admin/profile'),
};

export const adminProducts = {
  getAll: (params) => API.get('/admin/products', { params }),
  create: (data) => API.post('/admin/products', data),
  update: (id, data) => API.put(`/admin/products/${id}`, data),
  delete: (id) => API.delete(`/admin/products/${id}`),
};

export const adminCategories = {
  getAll: () => API.get('/admin/categories'),
  create: (data) => API.post('/admin/categories', data),
  update: (id, data) => API.put(`/admin/categories/${id}`, data),
  delete: (id) => API.delete(`/admin/categories/${id}`),
};

export const adminOrders = {
  getAll: (params) => API.get('/admin/orders', { params }),
  getDetail: (id) => API.get(`/admin/orders/${id}/detail`),
  exportAll: (params) => API.get('/admin/orders/export', { params }),
  updateStatus: (id, data) => API.put(`/admin/orders/${id}/status`, data),
  getDashboard: () => API.get('/admin/dashboard'),
};

export const adminReviews = {
  getPending: () => API.get('/admin/reviews/pending'),
  approve: (id) => API.put(`/admin/reviews/${id}/approve`),
  delete: (id) => API.delete(`/admin/reviews/${id}`),
};

export const adminCoupons = {
  getAll: () => API.get('/admin/coupons'),
  create: (data) => API.post('/admin/coupons', data),
  update: (id, data) => API.put(`/admin/coupons/${id}`, data),
  delete: (id) => API.delete(`/admin/coupons/${id}`),
};

// Admin accounts — superadmin-only on the backend
export const adminAccounts = {
  getAll: () => API.get('/admin/admins'),
  create: (data) => API.post('/admin/admins', data),
  update: (id, data) => API.put(`/admin/admins/${id}`, data),
  delete: (id) => API.delete(`/admin/admins/${id}`),
};

export const adminUsers = {
  getAll: (params) => API.get('/admin/users', { params }),
};

export const adminSettings = {
  getAll: () => API.get('/admin/settings'),
  update: (key, value) => API.put(`/admin/settings/${key}`, { value }),
};

export const adminUploads = {
  productImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return API.post('/admin/upload/product-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  productVideo: (file) => {
    const formData = new FormData();
    formData.append('video', file);
    return API.post('/admin/upload/product-video', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000, // 5 min for large videos
    });
  },
};

export default API;
